/*
 * Size guide modal.
 *
 * The "Size guide" link on the product page (`[data-sq-size-guide-open]`, with
 * `aria-controls` naming a dialog) opens that dialog as an on-page modal rather
 * than navigating to /pages/size-guide.
 *
 * The <sq-size-guide> element only holds the dialog and sits outside
 * <product-info>, so a variant change never re-renders it and its handlers live
 * for the whole page. The trigger lives inside the variant picker, which a
 * variant change CAN re-render, so clicks are caught by delegation on document
 * rather than a listener bound to the trigger element — that keeps working after
 * any dynamic update, with no duplicate handlers.
 *
 * Focus trap and focus return reuse the theme's global trapFocus / removeTrapFocus
 * (assets/global.js), the same utilities Dawn's own modals use.
 */
if (!customElements.get('sq-size-guide')) {
  class SizeGuideModal extends HTMLElement {
    constructor() {
      super();
      this.overlay = this.querySelector('[data-sq-sgm-overlay]');
      this.dialog = this.querySelector('[role="dialog"]');
      this.closeButton = this.querySelector('[data-sq-sgm-close]');
      this.trigger = null; // the element that opened it, for focus return

      this.onDocumentClick = this.onDocumentClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onOverlayClick = this.onOverlayClick.bind(this);
      this.close = this.close.bind(this);
    }

    connectedCallback() {
      // Delegated so a re-rendered trigger still works; removed on disconnect so
      // a reconnection never stacks a second listener.
      document.addEventListener('click', this.onDocumentClick);
      this.closeButton?.addEventListener('click', this.close);
      this.overlay?.addEventListener('mousedown', this.onOverlayClick);
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocumentClick);
      if (this.isOpen) this.unlock();
    }

    get isOpen() {
      return this.getAttribute('data-state') === 'open';
    }

    onDocumentClick(event) {
      const trigger = event.target.closest('[data-sq-size-guide-open]');
      if (!trigger) return;
      if (trigger.getAttribute('aria-controls') !== this.id) return;
      // Let modified clicks (open in new tab, etc.) use the real href.
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
        return;
      }
      event.preventDefault();
      this.open(trigger);
    }

    open(trigger) {
      if (this.isOpen) return;
      this.trigger = trigger || document.activeElement;
      this.hidden = false;
      // Force a reflow so the opening transition runs from the hidden state.
      // eslint-disable-next-line no-unused-expressions
      this.offsetWidth;
      this.setAttribute('data-state', 'open');
      document.body.classList.add('overflow-hidden');
      document.addEventListener('keydown', this.onKeydown);

      if (typeof trapFocus === 'function') {
        trapFocus(this.dialog, this.dialog);
      } else {
        this.dialog?.focus();
      }
      this.dispatchEvent(new CustomEvent('sq-size-guide:open', { bubbles: true }));
    }

    close() {
      if (!this.isOpen) return;
      this.setAttribute('data-state', 'closed');
      this.unlock();

      const returnTo = this.trigger;
      this.trigger = null;
      if (typeof removeTrapFocus === 'function') {
        removeTrapFocus(returnTo || null);
      } else if (returnTo) {
        returnTo.focus();
      }

      // Hide only after the closing transition, so it animates out. Guarded in
      // case it was reopened in the meantime.
      const onEnd = () => {
        if (!this.isOpen) this.hidden = true;
        this.dialog?.removeEventListener('transitionend', onEnd);
      };
      if (this.prefersReducedMotion()) {
        if (!this.isOpen) this.hidden = true;
      } else {
        this.dialog?.addEventListener('transitionend', onEnd);
        // Fallback if transitionend never fires.
        setTimeout(() => { if (!this.isOpen) this.hidden = true; }, 400);
      }
      this.dispatchEvent(new CustomEvent('sq-size-guide:close', { bubbles: true }));
    }

    unlock() {
      document.body.classList.remove('overflow-hidden');
      document.removeEventListener('keydown', this.onKeydown);
    }

    onKeydown(event) {
      if (event.key === 'Escape' || event.code === 'Escape') {
        event.preventDefault();
        this.close();
      }
    }

    onOverlayClick(event) {
      // Only a press that both starts and ends on the backdrop closes, so a
      // text selection that drifts onto the backdrop does not.
      if (event.target !== this.overlay) return;
      const up = (upEvent) => {
        this.overlay.removeEventListener('mouseup', up);
        if (upEvent.target === this.overlay) this.close();
      };
      this.overlay.addEventListener('mouseup', up);
    }

    prefersReducedMotion() {
      return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }
  }

  customElements.define('sq-size-guide', SizeGuideModal);
}
